import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * Catches the URL fragment `#session_id=<id>` after Emergent Managed Google Auth
 * redirects back. Exchanges the session_id with our backend for our own JWT
 * cookies, then routes the user into the app.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { googleSession } = useAuth();
  const hasProcessed = useRef(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : "";

    if (!sessionId) {
      setErrorMsg("Missing session_id in OAuth callback URL.");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
      return;
    }

    (async () => {
      const user = await googleSession(sessionId);
      // Always clear the hash so a refresh doesn't re-trigger.
      window.history.replaceState(null, "", window.location.pathname);
      if (user) {
        navigate("/groups", { replace: true });
      } else {
        setErrorMsg("Google sign-in could not be completed. Please try again.");
        setTimeout(() => navigate("/login", { replace: true }), 2500);
      }
    })();
  }, [googleSession, navigate]);

  return (
    <div
      className="min-h-screen grid place-items-center bg-cream"
      data-testid="auth-callback"
    >
      <div className="text-center space-y-3">
        {errorMsg ? (
          <>
            <AlertTriangle className="w-8 h-8 text-danger mx-auto" />
            <div className="text-sm text-danger font-semibold">{errorMsg}</div>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-olive mx-auto" />
            <div className="text-sm text-espresso/70">Completing Google sign-in…</div>
          </>
        )}
      </div>
    </div>
  );
}
